import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Image as HeroImage,
  Select,
  SelectItem,
  Spinner,
  Textarea,
} from '@heroui/react'

import { resolveGifUrl } from '../../gifs/services'
import { gifDisplayTitle } from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import { useToast } from '../../../hooks'
import {
  BridgeUnavailableError,
  getEvalCalibration,
  getEvalFixture,
  getEvalResult,
  getRetrievalRecordFor,
  listEvalResults,
  saveEvalCalibration,
  type ResultFileListItem,
  type RetrievalReviewItem,
} from '../services'
import type {
  GifClaim,
  JudgeCalibrationEntry,
  JudgeCalibrationFile,
  JudgeClaimDispute,
  JudgeDimensions,
  JudgeResult,
  ReportJson,
  RunResult,
  RunStance,
  StanceClaim,
} from '../../../../functions/tests/eval/recommendations/types'
import { curationDeepLink } from '../utils/curationDeepLink'

interface JudgeCalibrationProps {
  gifById: Map<string, Gif>
}

/** A single judged board (fixture × preset) queued for a calibration pass. */
interface Board {
  /** `fixtureId + '::' + presetName` — the calibration entry key. */
  key: string
  run: RunResult
}

/**
 * The five rubric dimensions in display order, with a short glanceable label and
 * the full name shown on hover so the abbreviation is decipherable in place.
 */
const DIMENSIONS: {
  key: keyof JudgeDimensions
  label: string
  full: string
}[] = [
  {
    key: 'moveSetCompleteness',
    label: 'Move-set',
    full: 'Move-set completeness',
  },
  { key: 'moveDistinctness', label: 'Distinct', full: 'Move distinctness' },
  { key: 'withinStanceFit', label: 'Within-fit', full: 'Within-stance fit' },
  {
    key: 'rationaleCoherence',
    label: 'Coherence',
    full: 'Rationale coherence',
  },
  { key: 'antiGeneric', label: 'Anti-generic', full: 'Anti-generic' },
]

/** Human labels for the claim-type groups on the finished panel. */
const CLAIM_TYPE_LABELS: Record<ClaimType, string> = {
  missing: 'missing move',
  stance: 'stance',
  fit: 'fit',
  generic: 'generic',
  rationale: 'rationale',
  notemove: 'note move',
}

type ClaimType =
  | 'missing'
  | 'notemove'
  | 'stance'
  | 'fit'
  | 'generic'
  | 'rationale'

function boardKey(fixtureId: string, presetName: string): string {
  return `${fixtureId}::${presetName}`
}

/**
 * The review state of a board, derived from its calibration entry (if any):
 *  - `unreviewed`  — no entry, or an entry with only in-progress disputes/notes
 *                    (no `reviewedAt`), autosaved when the curator jumped away.
 *  - `agreed`      — reviewed (has `reviewedAt`) with no disputes and no missed note.
 *  - `disputed`    — reviewed with at least one dispute or a missed-by-judge note.
 * Only an entry with `reviewedAt` counts as reviewed — that stamp is written
 * solely by the explicit agree/advance action, never by an autosave-on-jump.
 */
type ReviewState = 'unreviewed' | 'agreed' | 'disputed'

function reviewStateOf(entry: JudgeCalibrationEntry | undefined): ReviewState {
  if (!entry?.reviewedAt) return 'unreviewed'
  return entry.disputes.length > 0 || entry.missedByJudge
    ? 'disputed'
    : 'agreed'
}

// ── Stable claim keys ────────────────────────────────────────────────────────
// Each disputable claim gets a stable string key so a dispute survives re-loads
// and groups cleanly on the finished panel. Documented in recommendations-eval.md.
// These strings are load-bearing — an in-progress calibration file keys its
// dispute entries by them, so they must stay byte-identical across UI reworks.
const claimKey = {
  missing: (move: string) => `missing:${move}`,
  noteMove: (move: string) => `notemove:${move}`,
  stanceReal: (label: string) => `stance:${label}:real`,
  stanceDup: (label: string) => `stance:${label}:dup`,
  gifFit: (gifId: string) => `gif:${gifId}:fit`,
  gifGeneric: (gifId: string) => `gif:${gifId}:generic`,
  gifRationale: (gifId: string) => `gif:${gifId}:rationale`,
}

/** Classify a claim key into its type for the finished-panel grouping. */
function classifyClaim(key: string): ClaimType {
  if (key.startsWith('missing:')) return 'missing'
  if (key.startsWith('notemove:')) return 'notemove'
  if (key.startsWith('gif:')) {
    if (key.endsWith(':fit')) return 'fit'
    if (key.endsWith(':generic')) return 'generic'
    return 'rationale'
  }
  return 'stance'
}

/** A 0–3 score colored by band — red/amber/green — for glanceability. */
function scoreColor(
  score: number | null
): 'danger' | 'warning' | 'success' | 'default' {
  if (score === null) return 'default'
  if (score <= 1) return 'danger'
  if (score < 2.5) return 'warning'
  return 'success'
}

function fmtScore(score: number | null): string {
  return score === null ? '—' : score.toFixed(1)
}

/**
 * A collapsible block of long ground-truth prose (the fixture note / snippet).
 * Collapsed past a threshold so a wall of note text doesn't bury the board.
 * Prose renders at text-sm (readable) — this is the ground truth she compares
 * the board against.
 */
function Collapsible({
  title,
  text,
  defaultOpen = false,
  mono = false,
}: {
  title: string
  text: string
  defaultOpen?: boolean
  mono?: boolean
}) {
  const long = text.length > 320
  const [open, setOpen] = useState(defaultOpen || !long)
  return (
    <div className="rounded-lg border border-default-200 dark:border-default-100/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-default-500 hover:text-default-700"
        aria-expanded={open}
      >
        <span>{title}</span>
        {long && (
          <span aria-hidden className="text-default-400">
            {open ? '−' : '+'}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-default-200 dark:border-default-100/20 px-3 py-2.5">
          <p
            className={`whitespace-pre-wrap leading-relaxed text-default-700 dark:text-default-500 ${
              mono ? 'font-mono text-xs' : 'text-sm'
            }`}
          >
            {text}
          </p>
        </div>
      )}
    </div>
  )
}

export function JudgeCalibration({ gifById }: JudgeCalibrationProps) {
  const toast = useToast()
  const [results, setResults] = useState<ResultFileListItem[]>([])
  const [selectedFile, setSelectedFile] = useState<string>('')
  const [report, setReport] = useState<ReportJson | null>(null)
  const [loading, setLoading] = useState(true)
  const [bridgeDown, setBridgeDown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The judged boards in the selected report + supporting per-fixture data.
  const [boards, setBoards] = useState<Board[]>([])
  /** fixtureId → the fixture's conversation snippet (from meta.json on disk). */
  const [snippetByFixture, setSnippetByFixture] = useState<Map<string, string>>(
    new Map()
  )
  /** fixtureId → the fixture's traps id set (for highlighting on thumbnails). */
  const [trapsByFixture, setTrapsByFixture] = useState<
    Map<string, Set<string>>
  >(new Map())

  // Calibration state for the selected report.
  const [entries, setEntries] = useState<Record<string, JudgeCalibrationEntry>>(
    {}
  )
  const [cursor, setCursor] = useState(0)
  // Working state for the CURRENT board: disputed claim keys + their notes, and
  // the "judge missed something" text. Flushed into `entries` on advance.
  const [disputes, setDisputes] = useState<Record<string, string>>({})
  const [missedByJudge, setMissedByJudge] = useState('')
  const [saving, setSaving] = useState(false)

  // Load the results file list, default to newest.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setBridgeDown(false)
      setError(null)
      try {
        const list = await listEvalResults()
        if (cancelled) return
        setResults(list)
        setSelectedFile((prev) => prev || list[0]?.file || '')
      } catch (err) {
        if (cancelled) return
        if (err instanceof BridgeUnavailableError) setBridgeDown(true)
        else setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Load the selected report + its calibration file, then compute the judged
  // board queue and resume at the first un-reviewed board.
  const loadReport = useCallback(async (file: string) => {
    if (!file) return
    setLoading(true)
    setError(null)
    setReport(null)
    setBoards([])
    setEntries({})
    setCursor(0)
    setDisputes({})
    setMissedByJudge('')
    try {
      const data = await getEvalResult(file)
      // Only boards the judge actually scored — skip judge=null runs.
      const judged: Board[] = data.results
        .filter((r) => r.judge !== null)
        .map((r) => ({ key: boardKey(r.fixtureId, r.presetName), run: r }))

      // Fixtures referenced by the judged boards — load each once from disk to
      // get its conversation snippet (absent from the report JSON) and traps.
      const fixtureIds = Array.from(new Set(judged.map((b) => b.run.fixtureId)))
      const snippets = new Map<string, string>()
      const traps = new Map<string, Set<string>>()
      await Promise.all(
        fixtureIds.map(async (id) => {
          try {
            const meta = await getEvalFixture(id)
            const snip = meta.request?.context?.conversationSnippet
            if (snip) snippets.set(id, snip)
            traps.set(id, new Set(meta.expected?.traps ?? []))
          } catch {
            // Fixture unreadable — fall back to the report note only.
          }
        })
      )

      // Existing calibration (if any) — resume from it. v1 files (no `version`)
      // are ignored: starting fresh under the atomic-claims judge is intended.
      const existing = await getEvalCalibration(file)
      const loadedEntries =
        existing && existing.version === 2 ? (existing.entries ?? {}) : {}

      setReport(data)
      setBoards(judged)
      setSnippetByFixture(snippets)
      setTrapsByFixture(traps)
      setEntries(loadedEntries)
      // Resume: skip to the first board without a recorded review (an in-progress
      // entry that carries disputes/notes but no `reviewedAt` still counts as
      // unreviewed, so a jumped-away board is where she lands again).
      const firstUnreviewed = judged.findIndex(
        (b) => !loadedEntries[b.key]?.reviewedAt
      )
      setCursor(firstUnreviewed === -1 ? judged.length : firstUnreviewed)
    } catch (err) {
      if (err instanceof BridgeUnavailableError) setBridgeDown(true)
      else setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedFile) void loadReport(selectedFile)
  }, [selectedFile, loadReport])

  // Reviews autosave immediately — leaving mid-pass must never lose a call.
  // Writes are chained so rapid advances land in order; each write carries the
  // full entries snapshot from its own advance, so the last write always
  // persists the latest state.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const persist = useCallback(
    (file: string, nextEntries: Record<string, JudgeCalibrationEntry>) => {
      const payload: JudgeCalibrationFile = {
        reportFile: file,
        updatedAt: new Date().toISOString(),
        version: 2,
        entries: nextEntries,
      }
      saveChainRef.current = saveChainRef.current.then(async () => {
        setSaving(true)
        try {
          await saveEvalCalibration(file, payload)
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err)
          toast.error(`Autosave failed — review not persisted: ${msg}`)
        } finally {
          setSaving(false)
        }
      })
    },
    [toast]
  )

  const finished = cursor >= boards.length && boards.length > 0
  const current = !finished ? (boards[cursor] ?? null) : null

  // Seed the working dispute/missed state from whatever the current board
  // already carries (on board change only, so typing doesn't fight the effect).
  useEffect(() => {
    if (!current) return
    const entry = entries[current.key]
    const seeded: Record<string, string> = {}
    for (const d of entry?.disputes ?? []) seeded[d.claim] = d.note ?? ''
    setDisputes(seeded)
    setMissedByJudge(entry?.missedByJudge ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key])

  /** Toggle a claim's disputed state; keep any note already typed. */
  const toggleDispute = useCallback((claim: string) => {
    setDisputes((prev) => {
      if (claim in prev) {
        const next = { ...prev }
        delete next[claim]
        return next
      }
      return { ...prev, [claim]: '' }
    })
  }, [])

  const setDisputeNote = useCallback((claim: string, note: string) => {
    setDisputes((prev) => ({ ...prev, [claim]: note }))
  }, [])

  // Serialize the working dispute map into the persisted dispute list.
  const currentDisputeList = useCallback(
    (): JudgeClaimDispute[] =>
      Object.entries(disputes).map(([claim, note]) =>
        note.trim() ? { claim, note: note.trim() } : { claim }
      ),
    [disputes]
  )

  /**
   * Persist the current board's in-progress disputes/notes WITHOUT stamping
   * `reviewedAt` (only agree/advance does that) — the same autosave chain that
   * `advance` uses, so leaving a board via the navigator or undo never loses a
   * dispute or note. A prior `reviewedAt` (re-reviewing an already-reviewed
   * board) is preserved. When there's nothing to persist and no entry exists,
   * this is a no-op so idle jumps don't write empty entries.
   */
  const flushCurrent = useCallback(
    (
      entriesSnapshot: Record<string, JudgeCalibrationEntry>
    ): Record<string, JudgeCalibrationEntry> => {
      if (!current || !selectedFile) return entriesSnapshot
      const disputeList = currentDisputeList()
      const missed = missedByJudge.trim()
      const prior = entriesSnapshot[current.key]
      // Nothing in-progress and no existing entry → don't create an empty one.
      if (disputeList.length === 0 && !missed && !prior) return entriesSnapshot
      const entry: JudgeCalibrationEntry = { disputes: disputeList }
      if (missed) entry.missedByJudge = missed
      // Keep the reviewed stamp if this board was already reviewed; a plain
      // flush must never mark it reviewed, nor un-review it.
      if (prior?.reviewedAt) entry.reviewedAt = prior.reviewedAt
      const next = { ...entriesSnapshot, [current.key]: entry }
      setEntries(next)
      persist(selectedFile, next)
      return next
    },
    [current, selectedFile, currentDisputeList, missedByJudge, persist]
  )

  /** Commit the current board's disputes + missed note, then advance. */
  const advance = useCallback(() => {
    if (!current || !selectedFile) return
    const entry: JudgeCalibrationEntry = {
      disputes: currentDisputeList(),
      reviewedAt: new Date().toISOString(),
    }
    if (missedByJudge.trim()) entry.missedByJudge = missedByJudge.trim()
    const next = { ...entries, [current.key]: entry }
    setEntries(next)
    setCursor((c) => c + 1)
    persist(selectedFile, next)
  }, [
    current,
    selectedFile,
    currentDisputeList,
    missedByJudge,
    entries,
    persist,
  ])

  /**
   * Jump the cursor to any board, flushing the current board's in-progress
   * work first (never stamping it reviewed). Powers the board navigator and
   * the "next unreviewed" affordance.
   */
  const jumpTo = useCallback(
    (index: number) => {
      if (index < 0 || index > boards.length || index === cursor) return
      flushCurrent(entries)
      setCursor(index)
    },
    [boards.length, cursor, flushCurrent, entries]
  )

  const undo = useCallback(() => {
    if (cursor === 0) return
    // Flush the current board's in-progress work (without stamping it reviewed)
    // before stepping back, so a dispute typed here survives the move; the prior
    // board's entry stays recorded and re-seeds via the board-change effect.
    flushCurrent(entries)
    setCursor((c) => Math.max(0, c - 1))
  }, [cursor, flushCurrent, entries])

  /**
   * The first unreviewed board at or after the cursor, wrapping around — so it
   * still finds work when boards were reviewed out of order. Returns null when
   * every board is reviewed.
   */
  const nextUnreviewedIndex = useMemo(() => {
    if (boards.length === 0) return null
    for (let i = 0; i < boards.length; i++) {
      const idx = (cursor + 1 + i) % boards.length
      if (!entries[boards[idx].key]?.reviewedAt) return idx
    }
    return null
  }, [boards, cursor, entries])

  // Keyboard: a=advance (agree unless disputes), u=undo. Guarded so typing in a
  // textarea/input doesn't trigger navigation.
  useEffect(() => {
    if (!current) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return
      }
      if (e.key === 'a') advance()
      else if (e.key === 'u') undo()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, advance, undo])

  // Reviewed = an entry carrying `reviewedAt` (agree/advance stamped it). An
  // in-progress entry (autosaved disputes/notes from a jump) is NOT reviewed.
  const doneCount = useMemo(
    () => boards.filter((b) => entries[b.key]?.reviewedAt).length,
    [boards, entries]
  )
  const allReviewed = boards.length > 0 && doneCount === boards.length

  // Finished-panel tally: fully-agreed boards + dispute counts by claim type +
  // the missed-by-judge notes. Counts only REVIEWED boards, so it reflects
  // reviewed-so-far even when boards were reviewed out of order.
  const tally = useMemo(() => {
    let fullyAgreed = 0
    const byType: Record<ClaimType, number> = {
      missing: 0,
      notemove: 0,
      stance: 0,
      fit: 0,
      generic: 0,
      rationale: 0,
    }
    const missedNotes: {
      fixtureId: string
      presetName: string
      text: string
    }[] = []
    for (const b of boards) {
      const e = entries[b.key]
      if (!e?.reviewedAt) continue
      if (e.disputes.length === 0 && !e.missedByJudge) fullyAgreed++
      for (const d of e.disputes) byType[classifyClaim(d.claim)]++
      if (e.missedByJudge) {
        missedNotes.push({
          fixtureId: b.run.fixtureId,
          presetName: b.run.presetName,
          text: e.missedByJudge,
        })
      }
    }
    const totalDisputes = Object.values(byType).reduce((s, v) => s + v, 0)
    return { fullyAgreed, byType, totalDisputes, missedNotes }
  }, [boards, entries])

  const runLabel = selectedFile.replace(/\.json$/, '')

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-2">
        <div>
          <h2 className="text-lg font-semibold">Judge calibration</h2>
          <p className="text-sm text-default-500">
            Validate the atomic-claims judge by exception. Every claim sits with
            the thing it&apos;s about — note-move coverage in the header, stance
            calls per stance, and each gif&apos;s fit / generic / rationale
            calls right under the gif (a subtle ✓ means the judge passed it, so
            silent misses are findable in place). <strong>Dispute</strong> any
            call you think is wrong (with an optional note), and use{' '}
            <em>judge missed something</em> for anything it didn&apos;t flag at
            all. <kbd>a</kbd> → advance (no disputes = full agreement),{' '}
            <kbd>u</kbd> → undo. Jump to any board from the navigator below (or{' '}
            <em>next unreviewed</em>) — in-progress disputes/notes autosave on
            the move, but only <kbd>a</kbd> marks a board reviewed. Reviews save
            to <code>&lt;report&gt;.calibration.json</code> immediately — safe
            to leave mid-pass.
          </p>
        </div>
        {results.length > 0 && (
          <Select
            aria-label="Results file"
            size="sm"
            label="Eval run"
            className="max-w-md"
            selectedKeys={selectedFile ? new Set([selectedFile]) : new Set()}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0]
              if (typeof key === 'string') setSelectedFile(key)
            }}
          >
            {results.map((r) => (
              <SelectItem key={r.file}>
                {r.file.replace(/\.json$/, '')}
              </SelectItem>
            ))}
          </Select>
        )}
      </CardHeader>
      <CardBody>
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!loading && bridgeDown && (
          <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-950/30 text-warning-700 dark:text-warning-300 text-sm">
            Fixture file access requires the dev server — calibration reads and
            writes files under{' '}
            <code className="text-xs">
              functions/tests/eval/recommendations
            </code>
            .
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {!loading && !bridgeDown && !error && results.length === 0 && (
          <p className="text-sm text-default-500">
            No eval result files on disk. Run{' '}
            <code className="text-xs">npm run eval:recommendations</code> in{' '}
            <code className="text-xs">/functions</code> first.
          </p>
        )}

        {!loading && !bridgeDown && !error && report && boards.length === 0 && (
          <p className="text-sm text-default-500">
            No judged boards in {runLabel} — every run has{' '}
            <code>judge=null</code> (judge disabled or failed). Re-run with the
            judge on to calibrate.
          </p>
        )}

        {!loading && report && boards.length > 0 && (
          <BoardNavigator
            boards={boards}
            entries={entries}
            cursor={cursor}
            onJump={jumpTo}
          />
        )}

        {!loading && report && current && (
          <div className="mt-5 space-y-5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-mono text-xs text-default-500 truncate">
                  {current.run.fixtureId}
                </p>
                <Chip size="sm" variant="flat" color="secondary">
                  {current.run.presetName}
                </Chip>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saving && (
                  <span className="text-xs text-default-400">saving…</span>
                )}
                <p className="text-xs text-default-400">
                  {cursor + 1} / {boards.length}
                </p>
                {doneCount > 0 && (
                  <Chip size="sm" variant="flat" color="success">
                    {doneCount} reviewed
                  </Chip>
                )}
              </div>
            </div>

            <BoardView
              run={current.run}
              report={report}
              snippet={snippetByFixture.get(current.run.fixtureId) ?? null}
              traps={trapsByFixture.get(current.run.fixtureId) ?? new Set()}
              gifById={gifById}
              disputes={disputes}
              onToggleDispute={toggleDispute}
              onDisputeNote={setDisputeNote}
            />

            <div className="space-y-2">
              <Textarea
                label="Judge missed something? (optional)"
                placeholder="A move, gif, or problem the judge didn't flag at all"
                value={missedByJudge}
                onChange={(e) => setMissedByJudge(e.target.value)}
                minRows={2}
              />
              <div className="flex gap-2 flex-wrap items-center">
                <Button color="success" variant="flat" onPress={advance}>
                  {Object.keys(disputes).length === 0 && !missedByJudge.trim()
                    ? 'Agree — advance (a)'
                    : `Advance with ${Object.keys(disputes).length} dispute${
                        Object.keys(disputes).length === 1 ? '' : 's'
                      } (a)`}
                </Button>
                <Button
                  variant="light"
                  onPress={undo}
                  isDisabled={cursor === 0}
                >
                  Undo (u)
                </Button>
                <Button
                  variant="light"
                  color="secondary"
                  onPress={() =>
                    nextUnreviewedIndex !== null && jumpTo(nextUnreviewedIndex)
                  }
                  isDisabled={nextUnreviewedIndex === null}
                >
                  {nextUnreviewedIndex === null
                    ? 'All reviewed'
                    : 'Next unreviewed →'}
                </Button>
                {allReviewed && (
                  <Button
                    variant="light"
                    onPress={() => jumpTo(boards.length)}
                    className="ml-auto"
                  >
                    View summary
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && finished && (
          <div className="mt-5 space-y-4">
            <p className="text-sm">
              {allReviewed ? (
                <>
                  Calibrated all {boards.length} judged board
                  {boards.length === 1 ? '' : 's'} in{' '}
                </>
              ) : (
                <>
                  Reviewed {doneCount} of {boards.length} judged board
                  {boards.length === 1 ? '' : 's'} in{' '}
                </>
              )}
              <span className="font-mono text-xs">{runLabel}</span>.
              {!allReviewed && (
                <>
                  {' '}
                  {boards.length - doneCount} still unreviewed — jump to any
                  board above to finish the pass.
                </>
              )}
            </p>
            <div className="flex gap-2 flex-wrap items-center">
              <Chip size="sm" color="success" variant="flat">
                {tally.fullyAgreed} / {doneCount} reviewed boards fully agreed
              </Chip>
              <Chip size="sm" color="warning" variant="flat">
                {tally.totalDisputes} dispute
                {tally.totalDisputes === 1 ? '' : 's'}
              </Chip>
            </div>

            {tally.totalDisputes > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Disputes by claim type</p>
                <div className="flex gap-2 flex-wrap">
                  {(Object.keys(tally.byType) as ClaimType[])
                    .filter((t) => tally.byType[t] > 0)
                    .map((t) => (
                      <Chip key={t} size="sm" variant="flat" color="default">
                        {CLAIM_TYPE_LABELS[t]}: {tally.byType[t]}
                      </Chip>
                    ))}
                </div>
              </div>
            )}

            {tally.missedNotes.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Your notes on what the judge missed (
                  {tally.missedNotes.length})
                </p>
                {tally.missedNotes.map((m) => (
                  <div
                    key={boardKey(m.fixtureId, m.presetName)}
                    className="p-3 rounded-lg border border-default-200 dark:border-default-100/20"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs">{m.fixtureId}</span>
                      <Chip size="sm" variant="flat" color="secondary">
                        {m.presetName}
                      </Chip>
                    </div>
                    <p className="text-sm text-default-600 mt-1 italic">
                      “{m.text}”
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {nextUnreviewedIndex !== null && (
                <Button
                  variant="flat"
                  color="secondary"
                  onPress={() => jumpTo(nextUnreviewedIndex)}
                  className="w-fit"
                >
                  Go to next unreviewed
                </Button>
              )}
              <Button
                variant="flat"
                onPress={() => jumpTo(0)}
                className="w-fit"
              >
                Review from the start
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// ── Board navigator ──────────────────────────────────────────────────────────

/**
 * Abbreviate a long fixtureId for the navigator's per-fixture label so 30+
 * boards stay compact. Keeps the head and tail (usually the most identifying
 * parts) with an ellipsis between; short ids pass through unchanged.
 */
function abbreviateFixtureId(id: string): string {
  if (id.length <= 28) return id
  return `${id.slice(0, 16)}…${id.slice(-10)}`
}

/**
 * A dense navigator over every board in the report, grouped by fixture (one
 * abbreviated label per fixture, one small chip per preset beneath it). Each
 * chip is colored by its board's review state — unreviewed (outlined),
 * reviewed-agreed (success), reviewed-with-disputes (warning) — so a 30-board
 * pass is glanceable, and clicking a chip jumps the cursor there. The board at
 * the cursor gets a ring so the current position is unmistakable.
 */
function BoardNavigator({
  boards,
  entries,
  cursor,
  onJump,
}: {
  boards: Board[]
  entries: Record<string, JudgeCalibrationEntry>
  cursor: number
  onJump: (index: number) => void
}) {
  // Group boards by fixture, preserving first-seen order, and remember each
  // board's absolute index (the cursor target) alongside its preset name.
  const groups = useMemo(() => {
    const order: string[] = []
    const byFixture = new Map<string, { presetName: string; index: number }[]>()
    boards.forEach((b, index) => {
      const fx = b.run.fixtureId
      if (!byFixture.has(fx)) {
        byFixture.set(fx, [])
        order.push(fx)
      }
      byFixture.get(fx)!.push({ presetName: b.run.presetName, index })
    })
    return order.map((fx) => ({ fixtureId: fx, presets: byFixture.get(fx)! }))
  }, [boards])

  return (
    <div className="rounded-lg border border-default-200 p-3 dark:border-default-100/20">
      <div className="mb-2 flex items-center gap-3 text-[11px] text-default-400">
        <span className="font-medium uppercase tracking-wide">Boards</span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-success-500" />
          agreed
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-warning-500" />
          disputed
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full border border-default-300" />
          unreviewed
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {groups.map((g) => (
          <div key={g.fixtureId} className="min-w-0">
            <p
              className="mb-0.5 truncate font-mono text-[10px] text-default-400"
              title={g.fixtureId}
            >
              {abbreviateFixtureId(g.fixtureId)}
            </p>
            <div className="flex flex-wrap gap-1">
              {g.presets.map(({ presetName, index }) => {
                const state = reviewStateOf(entries[boards[index].key])
                const isCurrent = index === cursor
                const color =
                  state === 'agreed'
                    ? 'success'
                    : state === 'disputed'
                      ? 'warning'
                      : 'default'
                return (
                  <button
                    key={presetName}
                    type="button"
                    onClick={() => onJump(index)}
                    title={`${g.fixtureId} · ${presetName} · ${state}`}
                    aria-current={isCurrent ? 'true' : undefined}
                  >
                    <Chip
                      size="sm"
                      variant={state === 'unreviewed' ? 'bordered' : 'flat'}
                      color={color}
                      className={`cursor-pointer ${
                        isCurrent
                          ? 'ring-2 ring-secondary ring-offset-1 ring-offset-background'
                          : ''
                      }`}
                    >
                      {presetName}
                    </Chip>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Dispute affordance ───────────────────────────────────────────────────────

/**
 * A discoverable dispute toggle: a bordered "Dispute" button that flips to a
 * solid danger "Disputed ✓" when active, with the optional counter-argument
 * note appearing beneath only while disputed.
 */
function DisputeControl({
  claim,
  disputes,
  onToggle,
  onNote,
}: {
  claim: string
  disputes: Record<string, string>
  onToggle: (claim: string) => void
  onNote: (claim: string, note: string) => void
}) {
  const disputed = claim in disputes
  return (
    <div className="mt-2 space-y-2">
      <Button
        size="sm"
        variant={disputed ? 'solid' : 'light'}
        color={disputed ? 'danger' : 'default'}
        className={`h-6 min-w-0 px-2 text-[11px] ${
          disputed ? '' : 'text-default-400'
        }`}
        onPress={() => onToggle(claim)}
      >
        {disputed ? 'Disputed ✓' : 'dispute'}
      </Button>
      {disputed && (
        <Textarea
          aria-label="Dispute note"
          placeholder="Why the judge is wrong (optional)"
          value={disputes[claim]}
          onChange={(e) => onNote(claim, e.target.value)}
          minRows={1}
          size="sm"
        />
      )}
    </div>
  )
}

// ── Claim rows ───────────────────────────────────────────────────────────────

/** Shared prop bundle threaded down to every disputable row. */
type RowDisputeProps = {
  disputes: Record<string, string>
  onToggle: (claim: string) => void
  onNote: (claim: string, note: string) => void
}

/**
 * A single claim row that lives with its subject: an icon + headline + (when the
 * judge flagged something) its reasoning at readable text-sm, and a dispute
 * control. Passed claims render muted with a subtle ✓; flagged claims render
 * colored with the reasoning visible so misses are auditable in place.
 */
function ClaimRow({
  flagged,
  passIcon = '✓',
  flagIcon,
  headline,
  why,
  claim,
  disputes,
  onToggle,
  onNote,
}: {
  /** True = the judge flagged a problem (colored + why shown). */
  flagged: boolean
  /** Glyph for the passed state (default subtle check). */
  passIcon?: string
  /** Glyph for the flagged state. */
  flagIcon: string
  headline: React.ReactNode
  why: string
  claim: string
} & RowDisputeProps) {
  const disputed = claim in disputes
  return (
    <div
      className={`rounded-lg border p-2.5 ${
        disputed
          ? 'border-danger-300 bg-danger-50/50 dark:border-danger-800 dark:bg-danger-950/20'
          : flagged
            ? 'border-warning-200 bg-warning-50/40 dark:border-warning-900/50 dark:bg-warning-950/10'
            : 'border-default-200 dark:border-default-100/15'
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className={`mt-px shrink-0 ${
            flagged
              ? 'text-warning-600 dark:text-warning-500'
              : 'text-success-500'
          }`}
        >
          {flagged ? flagIcon : passIcon}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm ${
              flagged ? 'text-default-700' : 'text-default-500'
            }`}
          >
            {headline}
          </p>
          {flagged && why && (
            <p className="mt-1 text-sm leading-snug text-default-600 dark:text-default-500">
              {why}
            </p>
          )}
          <DisputeControl
            claim={claim}
            disputes={disputes}
            onToggle={onToggle}
            onNote={onNote}
          />
        </div>
      </div>
    </div>
  )
}

// ── Board gif (large, natural aspect) ────────────────────────────────────────

/**
 * A recommended gif rendered large at its natural aspect ratio (object-contain,
 * no forced square crop), rounded-lg to match the admin. Traps get a danger
 * ring. Clicking selects the gif's retrieval record into the stance's shared
 * full-width peek slot.
 */
function BoardGif({
  gif,
  gifId,
  isTrap,
  selected,
  onSelect,
}: {
  gif: Gif | undefined
  gifId: string
  isTrap: boolean
  selected: boolean
  onSelect: () => void
}) {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let mounted = true
    if (!gif) return
    setFailed(false)
    resolveGifUrl(gif.gifUrl)
      .then((u) => {
        if (mounted) setUrl(u)
      })
      .catch(() => {
        if (mounted) setFailed(true)
      })
    return () => {
      mounted = false
    }
  }, [gif])

  const ring = isTrap
    ? 'ring-2 ring-danger'
    : selected
      ? 'ring-2 ring-secondary'
      : ''

  return (
    <div className="w-full space-y-1.5">
      {gif && !failed ? (
        <button
          type="button"
          onClick={onSelect}
          className={`block w-full overflow-hidden rounded-lg bg-default-100 dark:bg-default-50/5 ${ring}`}
          title="Show / hide this gif's retrieval record"
        >
          <HeroImage
            src={url === '' ? undefined : url}
            alt={gifDisplayTitle(gif)}
            radius="none"
            className="max-h-64 w-full object-contain"
            classNames={{
              wrapper: 'w-full !max-w-full flex items-center justify-center',
              img: 'max-h-64 w-full object-contain',
            }}
          />
        </button>
      ) : (
        <div
          className={`flex aspect-square w-full items-center justify-center rounded-lg bg-default-200 p-2 text-center text-xs text-default-500 dark:bg-default-100/10 ${
            isTrap ? 'ring-2 ring-danger' : ''
          }`}
        >
          {gif ? 'failed to load' : 'missing from library'}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        {isTrap && (
          <Chip size="sm" color="danger" variant="flat">
            trap
          </Chip>
        )}
        {gif && (
          <p className="min-w-0 flex-1 truncate text-xs text-default-500">
            {gifDisplayTitle(gif)}
          </p>
        )}
      </div>
      <p className="truncate font-mono text-[10px] text-default-400">{gifId}</p>
    </div>
  )
}

// ── Full-width retrieval-record peek ─────────────────────────────────────────

/** Fetch states for the lazily-loaded retrieval record. */
type PeekState =
  | { status: 'loading' }
  | { status: 'loaded'; record: RetrievalReviewItem | null }
  | { status: 'error'; message: string }

/** Small labeled list block (scenario examples / exclusion conditions). */
function RecordList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-default-500">
        {label}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-default-600 dark:text-default-500">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The retrieval record for the selected gif, rendered full-width beneath the
 * stance's gif row so the narrative wraps to a readable measure (the old
 * per-gif peek wrapped into a narrow column). Reuses the shared single-flight
 * record cache — the record fetches only when a gif is selected, never on load.
 */
function StancePeek({ gifId }: { gifId: string }) {
  const [state, setState] = useState<PeekState>({ status: 'loading' })

  useEffect(() => {
    let mounted = true
    setState({ status: 'loading' })
    getRetrievalRecordFor(gifId)
      .then((record) => {
        if (mounted) setState({ status: 'loaded', record })
      })
      .catch((err) => {
        if (mounted) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          })
        }
      })
    return () => {
      mounted = false
    }
  }, [gifId])

  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50/30 p-3 dark:border-secondary-900 dark:bg-secondary-950/20">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-default-500">
          What the record says
        </p>
        <p className="truncate font-mono text-[10px] text-default-400">
          {gifId}
        </p>
      </div>

      {state.status === 'loading' && (
        <div className="flex justify-center py-3">
          <Spinner size="sm" />
        </div>
      )}

      {state.status === 'error' && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Couldn&apos;t load the record: {state.message}
        </p>
      )}

      {state.status === 'loaded' && state.record === null && (
        <p className="text-sm text-default-500">
          No retrieval record for this gif.
        </p>
      )}

      {state.status === 'loaded' && state.record && (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-default-700 dark:text-default-500">
            {state.record.narrative}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <RecordList
              label="Use when"
              items={state.record.scenarioExamples ?? []}
            />
            <RecordList
              label="Don't use when"
              items={state.record.exclusionConditions ?? []}
            />
            <RecordList
              label="Distinctive"
              items={state.record.distinctiveFeatures ?? []}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <Chip size="sm" variant="flat" color="default">
              tone: {state.record.toneAffordances}
            </Chip>
            <Chip size="sm" variant="flat" color="default">
              perspective: {state.record.perspectiveAffordances}
            </Chip>
          </div>
        </div>
      )}

      <Button
        as="a"
        href={curationDeepLink(gifId)}
        target="_blank"
        rel="noopener noreferrer"
        size="sm"
        variant="light"
        className="mt-1 h-auto min-h-0 py-0.5 text-default-400"
      >
        Record looks off → fix in Curation
      </Button>
    </div>
  )
}

// ── Per-stance section ───────────────────────────────────────────────────────

/**
 * One returned stance: its label + rationale, the judge's stance-level claim
 * rows (real move / duplicate-of), then the stance's gifs rendered large with
 * their three per-gif claim rows beneath each, and a single full-width record
 * peek for whichever gif is selected.
 */
function StanceSection({
  stance,
  index,
  claimsByGif,
  gifById,
  traps,
  disputes,
  onToggle,
  onNote,
}: {
  stance: RunStance
  index: number
  claimsByGif: Map<string, GifClaim>
  gifById: Map<string, Gif>
  traps: Set<string>
  disputes: Record<string, string>
  onToggle: (claim: string) => void
  onNote: (claim: string, note: string) => void
}) {
  const [peekGifId, setPeekGifId] = useState<string | null>(null)
  const rowProps: RowDisputeProps = { disputes, onToggle, onNote }

  return (
    <div className="rounded-lg border border-default-200 p-4 dark:border-default-100/20">
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-medium text-default-400">
          Stance {index + 1}
        </span>
        <p className="text-base font-semibold">{stance.label}</p>
      </div>
      {stance.rationale && (
        <p className="mt-1 text-sm leading-relaxed text-default-600 dark:text-default-500">
          {stance.rationale}
        </p>
      )}

      {/* Gifs, each with its three claim rows beneath. */}
      {stance.gifIds.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-4">
          {stance.gifIds.map((gifId) => {
            const gif = gifById.get(gifId)
            const gc = claimsByGif.get(gifId)
            return (
              <div
                key={gifId}
                className="w-full max-w-56 space-y-2 sm:max-w-64"
              >
                <BoardGif
                  gif={gif}
                  gifId={gifId}
                  isTrap={traps.has(gifId)}
                  selected={peekGifId === gifId}
                  onSelect={() =>
                    setPeekGifId((prev) => (prev === gifId ? null : gifId))
                  }
                />
                {gc ? (
                  <div className="space-y-1.5">
                    <ClaimRow
                      flagged={gc.fit !== 'yes'}
                      flagIcon="🎯"
                      headline={
                        gc.fit === 'yes' ? (
                          <>
                            <strong>fits</strong> the stance
                          </>
                        ) : (
                          <>
                            <strong>
                              {gc.fit === 'no' ? 'misfit' : 'partial fit'}
                            </strong>{' '}
                            for the stance
                          </>
                        )
                      }
                      why={gc.fitWhy}
                      claim={claimKey.gifFit(gifId)}
                      {...rowProps}
                    />
                    <ClaimRow
                      flagged={gc.generic}
                      flagIcon="🫧"
                      headline={
                        gc.generic ? (
                          <strong>generic crowd-pleaser</strong>
                        ) : (
                          <>
                            <strong>specific</strong> — not a catch-all
                          </>
                        )
                      }
                      why={gc.genericWhy}
                      claim={claimKey.gifGeneric(gifId)}
                      {...rowProps}
                    />
                    <ClaimRow
                      flagged={gc.rationaleHonest !== 'yes'}
                      flagIcon="🗣"
                      headline={
                        gc.rationaleHonest === 'yes' ? (
                          <>
                            <strong>honest</strong> rationale
                          </>
                        ) : (
                          <>
                            <strong>
                              {gc.rationaleHonest === 'no'
                                ? 'dishonest'
                                : 'partly-honest'}
                            </strong>{' '}
                            rationale
                          </>
                        )
                      }
                      why={gc.rationaleWhy}
                      claim={claimKey.gifRationale(gifId)}
                      {...rowProps}
                    />
                  </div>
                ) : (
                  <p className="text-xs italic text-default-400">
                    No judge claim for this gif.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Single full-width record peek for the selected gif in this stance. */}
      {peekGifId && (
        <div className="mt-4">
          <StancePeek key={peekGifId} gifId={peekGifId} />
        </div>
      )}
    </div>
  )
}

// ── Board view ───────────────────────────────────────────────────────────────

/**
 * The read-only board with claims attached to their subjects: ground truth +
 * computed dimensions + verdict + note-move coverage in the header, then one
 * section per returned stance carrying its own stance- and gif-level claims.
 */
function BoardView({
  run,
  report,
  snippet,
  traps,
  gifById,
  disputes,
  onToggleDispute,
  onDisputeNote,
}: {
  run: RunResult
  report: ReportJson
  snippet: string | null
  traps: Set<string>
  gifById: Map<string, Gif>
  disputes: Record<string, string>
  onToggleDispute: (claim: string) => void
  onDisputeNote: (claim: string, note: string) => void
}) {
  const fixtureMeta = report.fixtures.find((f) => f.id === run.fixtureId)
  const judge = run.judge as JudgeResult | null

  if (!judge) return null
  const claims = judge.claims

  const rowProps: RowDisputeProps = {
    disputes,
    onToggle: onToggleDispute,
    onNote: onDisputeNote,
  }

  // Which note moves the judge flagged unserved, and each one's why.
  const missingByMove = new Map(claims.missingMoves.map((m) => [m.move, m.why]))
  // The judge's gif claims, keyed by gifId for lookup while rendering stances.
  const claimsByGif = new Map(claims.gifs.map((g) => [g.gifId, g]))
  // Stance claims keyed by label so a returned stance can pull its judge call.
  const stanceClaimByLabel = new Map(claims.stances.map((s) => [s.label, s]))

  return (
    <div className="space-y-5">
      {/* ── Board header: ground truth + dims + verdict + coverage ── */}
      <div className="space-y-3">
        {/* Ground truth: the conversation + the note she wrote. */}
        {snippet && (
          <Collapsible title="Conversation" text={snippet} mono defaultOpen />
        )}
        {fixtureMeta?.note && (
          <Collapsible
            title="Note — the viable moves (ground truth)"
            text={fixtureMeta.note}
            defaultOpen
          />
        )}

        {/* Computed dimensions row + overall. */}
        <div className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-900">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-default-500">
              Computed dimensions
            </p>
            <Chip size="sm" variant="solid" color={scoreColor(judge.overall)}>
              overall {fmtScore(judge.overall)} / 3
            </Chip>
          </div>
          <div className="flex flex-wrap gap-2">
            {DIMENSIONS.map(({ key, label, full }) => (
              <Chip
                key={key}
                size="sm"
                variant="flat"
                color={scoreColor(judge.dimensions[key])}
                title={full}
              >
                {label} {fmtScore(judge.dimensions[key])}
              </Chip>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-default-400">
            Hover a dimension for its full name. Each is 0–3, computed from the
            claims below.
          </p>
        </div>

        {/* The judge's prose verdict. */}
        {judge.verdict && (
          <div className="rounded-lg bg-default-50 p-3 dark:bg-default-100/5">
            <p className="text-xs font-medium uppercase tracking-wide text-default-500">
              Verdict
            </p>
            <p className="mt-1 text-sm leading-relaxed text-default-700 dark:text-default-400">
              {judge.verdict}
            </p>
          </div>
        )}

        {/* Note-move coverage: every extracted note move, covered ✓ or missing. */}
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Note-move coverage{' '}
            <span className="font-normal text-default-400">
              ({claims.noteMoves.length - missingByMove.size}/
              {claims.noteMoves.length} served)
            </span>
          </p>
          {claims.noteMoves.length === 0 ? (
            <p className="text-sm text-default-500">
              The judge extracted no note moves from the ground-truth note.
            </p>
          ) : (
            <div className="space-y-1.5">
              {claims.noteMoves.map((move) => {
                const missing = missingByMove.has(move)
                return (
                  <ClaimRow
                    key={
                      missing ? claimKey.missing(move) : claimKey.noteMove(move)
                    }
                    flagged={missing}
                    flagIcon="🕳"
                    headline={
                      missing ? (
                        <>
                          <strong>missing</strong> — <em>{move}</em>
                        </>
                      ) : (
                        <>
                          <strong>served</strong> — <em>{move}</em>
                        </>
                      )
                    }
                    why={missing ? (missingByMove.get(move) ?? '') : ''}
                    claim={
                      missing ? claimKey.missing(move) : claimKey.noteMove(move)
                    }
                    {...rowProps}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Returned board: one section per stance, claims attached ── */}
      {run.stances.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm font-medium">Returned board</p>
          {run.stances.map((stance, i) => {
            const sc = stanceClaimByLabel.get(stance.label)
            return (
              <div key={`${stance.label}-${i}`} className="space-y-3">
                <StanceSection
                  stance={stance}
                  index={i}
                  claimsByGif={claimsByGif}
                  gifById={gifById}
                  traps={traps}
                  disputes={disputes}
                  onToggle={onToggleDispute}
                  onNote={onDisputeNote}
                />
                {/* Stance-level judge calls sit just under the stance card. */}
                {sc && <StanceClaimRows stance={sc} {...rowProps} />}
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-default-500">
          The workflow returned no stance groups for this board.
        </p>
      )}
    </div>
  )
}

/**
 * The stance-level claim rows (real-move call + duplicate-of call) for one
 * returned stance, rendered beneath its section. A duplicate row only appears
 * when the judge marked the stance a duplicate.
 */
function StanceClaimRows({
  stance,
  disputes,
  onToggle,
  onNote,
}: {
  stance: StanceClaim
} & RowDisputeProps) {
  const rowProps: RowDisputeProps = { disputes, onToggle, onNote }
  const realFlagged = stance.realMove === false
  const isDup = stance.duplicateOf != null
  return (
    <div className="ml-4 space-y-1.5 border-l-2 border-default-100 pl-4 dark:border-default-100/20">
      <ClaimRow
        flagged={realFlagged}
        flagIcon="🎭"
        headline={
          realFlagged ? (
            <>
              <strong>fake / off-note stance</strong> — not a viable move here
            </>
          ) : (
            <>
              <strong>real move</strong> — a viable response for this
              conversation
            </>
          )
        }
        why={stance.why}
        claim={claimKey.stanceReal(stance.label)}
        {...rowProps}
      />
      {isDup && (
        <ClaimRow
          flagged
          flagIcon="👥"
          headline={
            <>
              <strong>duplicate stance</strong> — repeats “{stance.duplicateOf}”
            </>
          }
          why={stance.why}
          claim={claimKey.stanceDup(stance.label)}
          {...rowProps}
        />
      )}
    </div>
  )
}
